import {
  Call,
  Download,
  Warning,
} from '@mui/icons-material';
import {
  Button,
  Card,
  Tab,
  TabList,
  TabPanel,
  Tabs,
  Theme,
  Typography,
  useTheme,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  AccordionGroup,
  Tooltip,
  Alert,
} from '@mui/joy';
import React, {
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useParams } from 'react-router-dom';
import { getAPI } from 'src/api';
import { GetListingResponse, ListingBuyerNDAStatus } from 'src/generated/api';
import { tss } from 'tss-react';
import { formatFileSize } from 'src/utils';
import PDFViewer from 'src/components/PDFViewer';
import PDFMultiPageViewer from 'src/components/PDFMultiPageViewer';
import { getConfig } from 'src/config';
import { logEvent } from 'src/analytics';
import { NDAModal } from './components/NDAModal';
import { ContactModal } from './components/ContactModal';
import { loggingEvents } from './loggingEvents';
import { ListingAuthContext } from './ListingAuthContext';

const useStyles = tss
  .withParams<{ theme: Theme }>()
  .create(({ theme }) => ({
    container: {
      display: 'flex',
      flexDirection: 'column',
      maxWidth: 1200,
      marginLeft: 'auto',
      marginRight: 'auto',
      width: '100%',
      marginTop: theme.spacing(4),
      marginBottom: theme.spacing(4),
      gap: theme.spacing(3),
    },
    card: {
      boxShadow: theme.shadow.sm,
      borderRadius: theme.radius.sm,
      padding: theme.spacing(2),
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(1),
    },
    sideCard: {
      width: 300,
    },
    fullWidthCard: {
      width: '100%',
    },
    stats: {
      display: 'flex',
      flexDirection: 'row',
      gap: theme.spacing(4),
      marginTop: theme.spacing(2),
    },
    statCard: {
      display: 'flex',
      flexDirection: 'column',
      gap: 0,
      padding: theme.spacing(2),
      width: 180,
    },
    row: {
      display: 'flex',
      flexDirection: 'row',
      gap: theme.spacing(3),
      alignItems: 'flex-start',
    },
    file: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginLeft: theme.spacing(3),
    },
    column: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(3),
    },
    sectionHeader: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: theme.spacing(2),
    },
    submitNdaButton: {
      marginTop: theme.spacing(1),
    },
    fullWidthTabs: {
      width: '100%',
    },
    fullWidthTab: {
      flex: 1,
    },
    fileList: {
      display: 'flex',
      flexDirection: 'column',
    },
    fileRow: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginLeft: theme.spacing(4),
    },
    fileRowBorder: {
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
    hiddenTab: {
      display: 'none',
    },
    alertIcon: {
      width: 18,
      height: 18,
    },
    alert: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: 0,
    },
    alertTitle: {
      display: 'flex',
      flexDirection: 'row',
      gap: 4,
      alignItems: 'center',
      textAlign: 'left',
    },
  }));

export default function Listing() {
  const theme = useTheme();
  const { id } = useParams();
  const { user } = useContext(ListingAuthContext);
  const [activeTab, setActiveTab] = useState<'teaser' | 'cim' | 'dataRoom'>('cim');
  const [showNDAModal, setShowNDAModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [listing, setListing] = useState<GetListingResponse | null>(null);
  const api = getAPI();
  const fetchListing = useCallback(async () => {
    const res = await api.listings.getListing(id!);
    setListing(res.data);
    if (
      res.data.ndaRequestStatus !== null &&
      res.data.ndaRequestStatus !== ListingBuyerNDAStatus.REQUESTED
    ) {
      setShowNDAModal(false);
    }
  }, [id, api]);

  useEffect(() => {
    fetchListing();
    logEvent({
      name: loggingEvents.listing.view,
      type: 'view',
      extra: {
        listingId: id ?? '',
      },
    });
  }, [fetchListing, id, user]);

  useEffect(() => {
    if (listing?.ndaRequestStatus === ListingBuyerNDAStatus.RELEASED) {
      api.listings.logListingOpen(id!);
    }
  }, [id, api, listing?.ndaRequestStatus]);

  const { classes, cx } = useStyles({ theme });

  const config = getConfig();
  const ndaReleased = listing?.ndaRequestStatus === ListingBuyerNDAStatus.RELEASED;
  const teaserUrl = `${config.hosts.api}/listings/${id}/download_blind_profile`;
  const cimUrl = `${config.hosts.api}/listings/${id}/download_cim`;

  return (
    <div className={classes.container}>
      <div className={classes.card}>
        <Typography level='h3'>{listing?.name} (Project {listing?.codeName})</Typography>
        <Typography level='body-md'>{listing?.description}</Typography>
        <div className={classes.stats}>
          <Card className={classes.statCard}>
            <Typography level='h3'>{listing?.revenueStr}</Typography>
            <Typography level='body-sm'>Revenue (2023A)</Typography>
          </Card>
          <Card className={classes.statCard}>
            <Typography level='h3'>{listing?.grossProfitStr}</Typography>
            <Typography level='body-sm'>Gross Profit (2023A)</Typography>
          </Card>
          <Card className={classes.statCard}>
            <Typography level='h3'>{listing?.adjEbitdaStr}</Typography>
            <Typography level='body-sm'>Adj. EBITDA (2023A)</Typography>
          </Card>
        </div>
      </div>
      {
        ndaReleased && (
          <Alert color="primary" className={classes.alert}>
            <Typography level="body-lg" className={classes.alertTitle}>
              <Warning className={classes.alertIcon} />
              <b>Important</b>
            </Typography>
            <Typography level="body-md">
              Please review the materials and let us know by {listing?.scheduleMeetingDeadline || 'TBD'} if you would
              like to proceed to scheduling meetings with management or if you'd
              like to exit the process.
            </Typography>
          </Alert>
        )
      }
      <div className={classes.row}>
        {
          !ndaReleased && (
            <div className={cx(classes.card, classes.fullWidthCard)}>
              <div className={classes.sectionHeader}>
                <Typography level='h3'>Investment Teaser</Typography>
                <Button
                  variant='outlined'
                  startDecorator={<Download />}
                  onClick={() => window.open(teaserUrl, '_blank')}
                >
                  Download
                </Button>
              </div>
              <PDFMultiPageViewer
                horizontal
                url={teaserUrl}
                widthPerPage={420}
                showPages={[1, 2]}
              />
            </div>
          )
        }
        {
          ndaReleased && (
            <Tabs
              className={cx(classes.card, classes.fullWidthCard)}
              value={activeTab}
              onChange={(_, value) => setActiveTab(value as 'teaser' | 'cim' | 'dataRoom')}
            >
              <TabList className={classes.fullWidthTabs}>
                <Tab className={classes.fullWidthTab} value='teaser'>Teaser</Tab>
                <Tab className={classes.fullWidthTab} value='cim'>CIM</Tab>
                <Tab className={classes.fullWidthTab} value='dataRoom'>Data Room</Tab>
              </TabList>
              <div className={activeTab !== 'teaser' ? classes.hiddenTab : undefined}>
                <div className={classes.sectionHeader}>
                  <Typography level='h3'>Investment Teaser</Typography>
                  <Button
                    variant='outlined'
                    startDecorator={<Download />}
                    onClick={() => window.open(`${teaserUrl}?isFileDownload=true`, '_blank')}
                  >
                    Download
                  </Button>
                </div>
                <PDFMultiPageViewer
                  horizontal
                  url={teaserUrl}
                  widthPerPage={420}
                  showPages={[1, 2]}
                />
              </div>
              <div className={activeTab !== 'cim' ? classes.hiddenTab : undefined}>
                <div className={classes.sectionHeader}>
                  <Typography level='h3'>Confidential Information Memorandum</Typography>
                  <Button
                    variant='outlined'
                    startDecorator={<Download />}
                    onClick={() => window.open(`${cimUrl}?isFileDownload=true`, '_blank')}
                  >
                    Download
                  </Button>
                </div>
                <PDFViewer
                  url={cimUrl}
                  width={750}
                />
              </div>
              <TabPanel value='dataRoom'>
                <div className={classes.sectionHeader}>
                  <Typography level='h3'>Data Room</Typography>
                  <Button
                    variant='soft'
                    startDecorator={<Download />}
                    onClick={() => window.open(`${config.hosts.api}/listings/${id}/data_room/download_all`, '_blank')}
                  >
                    Download All
                  </Button>
                </div>
                <AccordionGroup>
                  {listing?.dataRoom?.map((category) => (
                    <Accordion key={category.category} defaultExpanded>
                      <AccordionSummary>
                        {category.category} ({category.files.length})
                      </AccordionSummary>
                      <AccordionDetails>
                        <div className={classes.fileList}>
                          {category.files.map((file, index) => (
                            <div
                              key={file.name}
                              className={cx(
                                classes.fileRow,
                                index < category.files.length - 1 && classes.fileRowBorder,
                              )}
                            >
                              <Typography level='body-md'>{file.name} ({formatFileSize(file.size)})</Typography>
                              <Tooltip title='Download'>
                                <IconButton
                                  variant='plain'
                                  onClick={() => window.open(`${config.hosts.api}/listings/${id}/data_room/download_file?fileName=${encodeURIComponent(file.name)}&requestId=${file.requestId}`, '_blank')}
                                >
                                  <Download />
                                </IconButton>
                              </Tooltip>
                            </div>
                          ))}
                        </div>
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </AccordionGroup>
              </TabPanel>
            </Tabs>
          )
        }
        <div className={classes.column}>
          {
            (
              listing?.ndaRequestStatus === null ||
              listing?.ndaRequestStatus === ListingBuyerNDAStatus.REQUESTED
            ) && (
              <div className={cx(classes.card, classes.sideCard)}>
                <Typography level='h4'>Interested in this listing?</Typography>
                <Typography level='body-md'>
                  Submit an NDA request to view more information about this listing.
                </Typography>
                <Button
                  variant='solid'
                  color='primary'
                  size='lg'
                  className={classes.submitNdaButton}
                  onClick={() => {
                    setShowNDAModal(true);
                    logEvent({
                      name: loggingEvents.listing.requestNda,
                      type: 'click',
                      extra: {
                        listingId: id ?? '',
                      },
                    });
                  }}
                >
                  Request NDA
                </Button>
              </div>
            )
          }
          {
            (
              listing?.ndaRequestStatus === ListingBuyerNDAStatus.BUYER_SIGNED ||
              listing?.ndaRequestStatus === ListingBuyerNDAStatus.ADVISOR_APPROVED ||
              listing?.ndaRequestStatus === ListingBuyerNDAStatus.ADVISOR_REJECTED ||
              listing?.ndaRequestStatus === ListingBuyerNDAStatus.SELLER_SIGNED ||
              listing?.ndaRequestStatus === ListingBuyerNDAStatus.SELLER_REJECTED
            ) && (
              <div className={cx(classes.card, classes.sideCard)}>
                <Typography level='h4'>NDA Submitted</Typography>
                <Typography level='body-md'>
                  Your NDA has been submitted.
                  You will receive an email when the seller has accepted your request.
                </Typography>
              </div>
            )
          }
          {
            ndaReleased && (
              <div className={cx(classes.card, classes.sideCard)}>
                <Typography level='h4'>Data Room Unlocked</Typography>
                <Typography level='body-md'>
                  Your NDA has been accepted by the seller. You can now access and download
                  confidential transaction materials.
                </Typography>
              </div>
            )
          }
          {
            (
              listing?.ndaRequestStatus === ListingBuyerNDAStatus.REMOVED_BY_ADVISOR_AFTER_NDA ||
              listing?.ndaRequestStatus === ListingBuyerNDAStatus.REMOVED_BY_OWNER_AFTER_NDA
            ) && (
              <div className={cx(classes.card, classes.sideCard)}>
                <Typography level='h4'>Removed from Process</Typography>
                <Typography level='body-md'>
                  You have been removed from the process for this listing.
                  Please contact the M&A advisor if you have any questions.
                </Typography>
              </div>
            )
          }
          <div className={cx(classes.card, classes.sideCard)}>
            <Typography level='h4'>Have questions?</Typography>
            <Typography level='body-md'>
              Contact the M&A advisor for this listing.
            </Typography>
            <Button
              variant='outlined'
              color='neutral'
              size='lg'
              onClick={() => setShowContactModal(true)}
              className={classes.submitNdaButton}
            >
              <Call />
              Contact Us
            </Button>
          </div>
        </div>
      </div>
      {
        showNDAModal && (
          <NDAModal
            id={id!}
            onClose={() => {
              setShowNDAModal(false);
              fetchListing();
            }}
            ndaRequestStatus={listing?.ndaRequestStatus ?? null}
          />
        )
      }
      {
        showContactModal && (
          <ContactModal
            advisorName={listing?.advisorName}
            advisorEmail={listing?.advisorEmail}
            advisorPhone={listing?.advisorPhone}
            onClose={() => setShowContactModal(false)}
          />
        )
      }
    </div>
  );
}
